import type { FC } from "react"
import { useCallback, useRef, useState } from "react"

import styles from "../TaigaPage.module.scss"

type CreateMentionModalProps = {
	defaultPriority: number
	isCreating: boolean
	onClose: () => void
	onCreate: (text: string, priority: number, mentionMaki: boolean) => void
}

const CreateMentionModal: FC<CreateMentionModalProps> = ({
	defaultPriority,
	isCreating,
	onClose,
	onCreate,
}) => {
	const [text, setText] = useState("")
	const [priority, setPriority] = useState(defaultPriority)
	// maki 宛メンションはデフォルト ON。toggle off で自分宛のみにできる。
	const [mentionMaki, setMentionMaki] = useState(true)
	// fable あり選択時は本文末尾に「 fable」を追記して投稿する
	const [withFable, setWithFable] = useState(false)
	const textareaRef = useRef<HTMLTextAreaElement>(null)

	const handleSubmit = useCallback(() => {
		const trimmed = text.trim()
		if (!trimmed) return
		onCreate(withFable ? `${trimmed} fable` : trimmed, priority, mentionMaki)
	}, [text, priority, mentionMaki, withFable, onCreate])

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault()
				onClose()
			}
			// Cmd/Ctrl + Enter で送信
			if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
				e.preventDefault()
				handleSubmit()
			}
		},
		[onClose, handleSubmit]
	)

	return (
		<div
			className={styles.createMentionOverlay}
			onClick={onClose}
			onKeyDown={handleKeyDown}
		>
			<div
				className={styles.createMentionModal}
				onClick={(e) => e.stopPropagation()}
			>
				<h3 className={styles.createMentionTitle}>メンションを手動作成</h3>

				<div className={styles.createMentionField}>
					<label
						className={styles.createMentionLabel}
						htmlFor="create-mention-text"
					>
						本文
					</label>
					<textarea
						ref={textareaRef}
						id="create-mention-text"
						className={styles.createMentionTextarea}
						value={text}
						onChange={(e) => setText(e.target.value)}
						placeholder="メンション本文を入力..."
						rows={4}
						// biome-ignore lint/a11y/noAutofocus: modal open で即 focus
						autoFocus
						disabled={isCreating}
					/>
				</div>

				<div className={styles.createMentionField}>
					<span className={styles.createMentionLabel}>優先度</span>
					<div className={styles.createMentionPriorityButtons}>
						{[0, 1, 2, 3, 4, 5].map((p) => (
							<button
								key={p}
								type="button"
								className={`${styles.createMentionPriorityButton} ${
									priority === p ? styles.createMentionPriorityButtonActive : ""
								}`}
								onClick={() => setPriority(p)}
								disabled={isCreating}
							>
								{p}
							</button>
						))}
						<input
							id="create-mention-priority"
							type="number"
							className={`${styles.createMentionPriorityManualInput} ${
								priority > 5
									? styles.createMentionPriorityManualInputActive
									: ""
							}`}
							value={priority > 5 ? priority : ""}
							placeholder="6+"
							onChange={(e) => {
								const v = Number.parseInt(e.target.value, 10)
								if (!Number.isNaN(v) && v >= 0) setPriority(v)
							}}
							min={6}
							disabled={isCreating}
							aria-label="優先度を手動入力 (6以上)"
						/>
					</div>
					<span className={styles.createMentionHint}>
						小さいほど高優先 (0=最優先, 2=通常)。6 以上は右端に手入力
					</span>
				</div>

				<div className={styles.createMentionField}>
					<span className={styles.createMentionLabel}>fable</span>
					<div className={styles.createMentionFableButtons}>
						<button
							type="button"
							className={`${styles.createMentionFableButton} ${
								!withFable ? styles.createMentionFableButtonActive : ""
							}`}
							onClick={() => setWithFable(false)}
							disabled={isCreating}
						>
							fable なし
						</button>
						<button
							type="button"
							className={`${styles.createMentionFableButton} ${
								withFable ? styles.createMentionFableButtonActive : ""
							}`}
							onClick={() => setWithFable(true)}
							disabled={isCreating}
						>
							fable あり
						</button>
					</div>
					<span className={styles.createMentionHint}>
						あり: 本文末尾に「 fable」を追記して投稿
					</span>
				</div>

				<div className={styles.createMentionField}>
					<label className={styles.createMentionCheckbox}>
						<input
							type="checkbox"
							checked={mentionMaki}
							onChange={(e) => setMentionMaki(e.target.checked)}
							disabled={isCreating}
						/>
						maki にメンションする
					</label>
					<span className={styles.createMentionHint}>
						ON で maki 宛メンションも付けて投稿（embed session 起動用）
					</span>
				</div>

				<div className={styles.createMentionActions}>
					<button
						type="button"
						className={styles.createMentionCancelBtn}
						onClick={onClose}
						disabled={isCreating}
					>
						キャンセル
					</button>
					<button
						type="button"
						className={styles.createMentionSubmitBtn}
						onClick={handleSubmit}
						disabled={!text.trim() || isCreating}
					>
						{isCreating ? "作成中..." : "作成"}
					</button>
				</div>
			</div>
		</div>
	)
}

export default CreateMentionModal
